import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { AdminAttentionBell } from '@/components/admin/AdminAttentionBell';
import { Button } from '@/components/ui/button';
import { FarmlyAvatar } from '@/components/farmly/FarmlyAvatar';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { TopbarContextSwitcher } from './TopbarContextSwitcher';
import { useActivePortal } from '@/context/ActivePortalContext';
import { ProducerHeaderStatus } from '@/components/producer/ProducerHeaderStatus';
import { AccountMenu } from '@/components/nav/AccountMenu';
import { useAccountMenuProps } from '@/hooks/useAccountMenuProps';

export type RoleBadge = 'admin' | 'producer' | 'customer' | 'editor';

interface AppTopbarProps {
  title?: string;
  role: RoleBadge;
  userEmail?: string | null;
  className?: string;
  currentProducerId?: string;
  currentProducerName?: string;
  isNarrow?: boolean;
}

export function AppTopbar({ 
  title, 
  role, 
  userEmail, 
  className,
  currentProducerId,
  currentProducerName,
  isNarrow = false,
}: AppTopbarProps) {
  const { pathname } = useLocation();
  const isAdminRoute = pathname.startsWith('/app/admin');
  const { activeMode, setMode } = useActivePortal();
  const accountProps = useAccountMenuProps();

  const initials = userEmail
    ? userEmail.slice(0, 2).toUpperCase()
    : 'U';

  return (
    <>
    <header
      className={cn(
        'sticky top-0 z-30 flex h-14 items-center gap-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4',
        className
      )}
    >
      {/* Always show sidebar trigger — gear icon replaced by "Mer" in bottom nav */}
      <SidebarTrigger className="-ml-1" />

      <div className="flex flex-1 items-center gap-3 min-w-0">
        <TopbarContextSwitcher
          mode={role}
          currentProducerId={currentProducerId}
          currentProducerName={currentProducerName}
        />
        {role === 'producer' && currentProducerId && (
          <ProducerHeaderStatus farmId={currentProducerId} />
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {isAdminRoute ? <AdminAttentionBell /> : <NotificationBell />}
        <AccountMenu
          {...accountProps}
          activeMode={activeMode}
          onSetMode={setMode}
          trigger={
            <Button variant="ghost" size="sm" className="flex items-center gap-2 h-9 px-2">
              <FarmlyAvatar initials={initials} size="xs" />
              <span className="hidden sm:inline text-sm text-muted-foreground max-w-[140px] truncate">
                {userEmail}
              </span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </Button>
          }
        />
      </div>
    </header>

    </>
  );
}
